import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";

type Step = "brand" | "campaign" | "generate";

const VOICES = [
  "professional",
  "friendly",
  "bold",
  "witty",
  "educational",
  "inspirational",
];
const PLATFORMS = [
  { id: "instagram_feed", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "twitter", label: "X / Twitter" },
  { id: "facebook", label: "Facebook" },
];

interface Props {
  onComplete: (brandId: string) => void;
}

export default function Onboarding({ onComplete }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("brand");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Brand form state
  const [brandName, setBrandName] = useState("");
  const [voice, setVoice] = useState("friendly");
  const [industry, setIndustry] = useState("");
  const [audience, setAudience] = useState("");
  const [brandId, setBrandId] = useState<string | null>(null);

  // Campaign form state
  const [campaignName, setCampaignName] = useState("");
  const [product, setProduct] = useState("");
  const [platforms, setPlatforms] = useState<string[]>([
    "instagram_feed",
    "linkedin",
  ]);
  const [campaignId, setCampaignId] = useState<string | null>(null);

  // Generation preferences
  const [postVariationsPerPlatform, setPostVariationsPerPlatform] = useState(1);

  const togglePlatform = (id: string) =>
    setPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );

  const handleCreateBrand = async () => {
    if (!brandName.trim()) return setError("Brand name is required");
    setSaving(true);
    setError(null);
    try {
      const { brand } = await api.brands.create({
        name: brandName,
        voice: voice as never,
        industry,
        targetAudience: audience,
      });
      setBrandId(brand.brandId);
      setStep("campaign");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignName.trim() || !brandId)
      return setError("Campaign name is required");
    if (!platforms.length) return setError("Select at least one platform");
    setSaving(true);
    setError(null);
    try {
      const { campaign } = await api.campaigns.create(brandId, {
        name: campaignName,
        platforms: platforms as never,
        product: { name: product, description: "", differentiators: [] },
      });
      setCampaignId(campaign.campaignId);
      setStep("generate");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!brandId || !campaignId) return;
    setSaving(true);
    setError(null);
    try {
      // Save the variations preference to the user profile first
      await api.me.patchProfile({ postVariationsPerPlatform }).catch(() => {
        /* non-fatal — preference will be saved next time user visits Settings */
      });
      await api.generate.dispatch({
        brandId,
        campaignId,
        platforms,
        variationsCount: postVariationsPerPlatform,
      });
      // Signal App.tsx that brand creation + campaign are done, then go to
      // social setup so the user can connect accounts for this brand.
      onComplete(brandId);
      navigate(`/setup/social?brandId=${brandId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 to-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8">
          {(["brand", "campaign", "generate"] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step === s ? "bg-brand-500 text-white" : i < ["brand", "campaign", "generate"].indexOf(step) ? "bg-brand-200 text-brand-700" : "bg-gray-200 text-gray-500"}`}
              >
                {i + 1}
              </div>
              {i < 2 && <div className="h-px w-8 bg-gray-200" />}
            </div>
          ))}
          <span className="ml-2 text-sm text-gray-500">
            {step === "brand"
              ? "Create your brand"
              : step === "campaign"
                ? "Set up a campaign"
                : "Generate first batch"}
          </span>
        </div>

        <div className="card p-8">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2 mb-4">
              {error}
            </p>
          )}

          {/* Step 1: Brand */}
          {step === "brand" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">
                Tell us about your brand
              </h2>
              <div>
                <label className="label">Brand name *</label>
                <input
                  className="input"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Acme Coffee Co."
                />
              </div>
              <div>
                <label className="label">Industry</label>
                <input
                  className="input"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="Food & Beverage"
                />
              </div>
              <div>
                <label className="label">Target audience</label>
                <input
                  className="input"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="Coffee lovers aged 25–45, urban professionals"
                />
              </div>
              <div>
                <label className="label">Brand voice</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {VOICES.map((v) => (
                    <button
                      key={v}
                      onClick={() => setVoice(v)}
                      className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-colors ${voice === v ? "bg-brand-500 text-white border-brand-500" : "border-gray-300 text-gray-600 hover:border-brand-400"}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleCreateBrand}
                disabled={saving}
                className="btn-primary w-full mt-2"
              >
                {saving ? "Saving…" : "Continue →"}
              </button>
            </div>
          )}

          {/* Step 2: Campaign */}
          {step === "campaign" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">
                Create your first campaign
              </h2>
              <div>
                <label className="label">Campaign name *</label>
                <input
                  className="input"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Spring Launch 2026"
                />
              </div>
              <div>
                <label className="label">What are you promoting?</label>
                <input
                  className="input"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="Single-origin espresso subscription"
                />
              </div>
              <div>
                <label className="label">Platforms</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${platforms.includes(p.id) ? "bg-brand-500 text-white border-brand-500" : "border-gray-300 text-gray-600 hover:border-brand-400"}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleCreateCampaign}
                disabled={saving}
                className="btn-primary w-full mt-2"
              >
                {saving ? "Saving…" : "Continue →"}
              </button>
            </div>
          )}

          {/* Step 3: Generate */}
          {step === "generate" && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="text-5xl mb-2">✨</div>
                <h2 className="text-xl font-semibold">
                  You're set up. Let's make content.
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  MediaDingo will generate your first batch of posts using
                  Gemini. You'll land in the Review Queue to approve, edit, or
                  reject each one.
                </p>
              </div>

              {/* Variations preference */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    How many variations per platform?
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Each variation uses a different hook, angle, or tone. More
                    variations means more to choose from — and more quota used.
                  </p>
                </div>
                <div className="flex gap-2">
                  {([1, 2, 3] as const).map((n) => (
                    <button
                      key={n}
                      onClick={() => setPostVariationsPerPlatform(n)}
                      aria-pressed={postVariationsPerPlatform === n}
                      className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                        postVariationsPerPlatform === n
                          ? "bg-brand-500 text-white border-brand-500"
                          : "border-gray-300 text-gray-600 hover:border-brand-400 bg-white"
                      }`}
                    >
                      {n === 1
                        ? "1 — Standard"
                        : n === 2
                          ? "2 — More choice"
                          : "3 — Maximum"}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400">
                  {postVariationsPerPlatform === 1
                    ? `Generates ${platforms.length} post${platforms.length !== 1 ? "s" : ""} (1 per platform).`
                    : `Generates ${platforms.length * postVariationsPerPlatform} posts (${postVariationsPerPlatform} per platform). You can change this later in Settings.`}
                </p>
              </div>

              <button
                onClick={handleGenerate}
                disabled={saving}
                className="btn-primary w-full"
              >
                {saving ? "Generating…" : "Generate first batch"}
              </button>
              <button
                onClick={() => {
                  if (!brandId) return;
                  onComplete(brandId);
                  navigate(`/setup/social?brandId=${brandId}`);
                }}
                className="btn-secondary w-full"
              >
                Skip — I'll do it manually
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
